export const WIDGET_NATURAL_WIDTH = 722;
export const WIDGET_CONTENT_HEIGHT = 614;
export const WIDGET_HEADER_HEIGHT = 36;
export const WIDGET_BORDER_HEIGHT = 2;
export const WIDGET_CONTENT_ASPECT = WIDGET_NATURAL_WIDTH / WIDGET_CONTENT_HEIGHT;

export const DESKTOP_WIDGET_MARGIN = 24;
export const TABLE_CASCADE_OFFSET = 30;

export interface TablePosition {
    x: number
    y: number
}

export interface WidgetSize {
    width: number
    height: number
}

export interface ResizePointerOrigin {
    pointerX: number
    pointerY: number
    width: number
}

export interface ResizePointerPosition {
    x: number
    y: number
}

export const getWidgetContentHeight = (width: number): number => width * (WIDGET_CONTENT_HEIGHT / WIDGET_NATURAL_WIDTH);

export const getWidgetSize = (width: number): WidgetSize => ({
    height: WIDGET_HEADER_HEIGHT + WIDGET_BORDER_HEIGHT + getWidgetContentHeight(width),
    width
});

export const getMaxWidgetWidth = (): number => {
    if (typeof window === 'undefined') {
        return WIDGET_NATURAL_WIDTH;
    }

    const maxByViewportWidth = window.innerWidth;
    const maxContentHeight = window.innerHeight - WIDGET_HEADER_HEIGHT - WIDGET_BORDER_HEIGHT;
    const maxByViewportHeight = maxContentHeight * WIDGET_CONTENT_ASPECT;

    return Math.max(WIDGET_NATURAL_WIDTH, Math.min(maxByViewportWidth, maxByViewportHeight));
};

export const clampWidgetWidth = (width: number): number => {
    const maxWidth = getMaxWidgetWidth();
    return Math.min(Math.max(width, WIDGET_NATURAL_WIDTH), maxWidth);
};

export const clampTablePosition = (position: TablePosition, width: number): TablePosition => {
    if (typeof window === 'undefined') {
        return position;
    }

    const {height} = getWidgetSize(width);
    const maxX = Math.max(window.innerWidth - width, 0);
    const maxY = Math.max(window.innerHeight - height, 0);

    return {
        x: Math.min(Math.max(position.x, 0), maxX),
        y: Math.min(Math.max(position.y, 0), maxY)
    };
};

export const getDefaultTablePosition = (tableCount = 0, width = WIDGET_NATURAL_WIDTH): TablePosition => {
    if (typeof window === 'undefined') {
        return {x: 0, y: 0};
    }

    return clampTablePosition({
        x: DESKTOP_WIDGET_MARGIN + tableCount * TABLE_CASCADE_OFFSET,
        y: DESKTOP_WIDGET_MARGIN + tableCount * TABLE_CASCADE_OFFSET
    }, width);
};

export const computeResizedWidth = (
    start: ResizePointerOrigin,
    pointer: ResizePointerPosition
): number => {
    const dx = pointer.x - start.pointerX;
    const dy = pointer.y - start.pointerY;
    const startContentHeight = getWidgetContentHeight(start.width);
    const candidateFromWidth = start.width + dx;
    const candidateFromHeight = (startContentHeight + dy) * WIDGET_CONTENT_ASPECT;

    return clampWidgetWidth(Math.max(candidateFromWidth, candidateFromHeight));
};
